import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AdminRichTextEditor, {
  AdminRichTextEditorHandle,
} from '../../components/admin/AdminRichTextEditor'
import {
  AdminApi,
  AdminBlogPostPayload,
  AdminBlogPostStatus,
  AdminBlogSharingTypeOption,
  AdminBlogTopicOption,
  AdminCourse,
} from '../../services/adminApi'

const fieldClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-700'
const labelClass = 'mb-1 block text-xs font-semibold text-slate-700'
const panelClass = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm'
const btnPrimary =
  'rounded-lg bg-emerald-800 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-50'
const btnSecondary =
  'rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50'

const DEFAULT_QUOTE =
  'Muốn tìm hiểu thêm về chủ đề này thì tham khảo khóa học sau'

const formatFee = (fee?: number | null) => {
  if (fee == null || Number.isNaN(Number(fee))) return ''
  return `${Number(fee).toLocaleString('vi-VN')}đ`
}

const AdminPostFormPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = Boolean(id)
  const postId = id ? Number(id) : null

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [topics, setTopics] = useState<AdminBlogTopicOption[]>([])
  const [sharingTypes, setSharingTypes] = useState<AdminBlogSharingTypeOption[]>([])
  const [courses, setCourses] = useState<AdminCourse[]>([])

  const [title, setTitle] = useState('')
  const [topicId, setTopicId] = useState<number | ''>('')
  const [sharingTypeId, setSharingTypeId] = useState<number | ''>('')
  const [author, setAuthor] = useState('')
  const [status, setStatus] = useState<AdminBlogPostStatus>('INACTIVE')
  const [isFeatured, setIsFeatured] = useState(false)
  const [shortDescription, setShortDescription] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [postContent, setPostContent] = useState('')
  const contentEditorRef = useRef<AdminRichTextEditorHandle | null>(null)
  const [slug, setSlug] = useState('')
  const [uuid, setUuid] = useState('')
  const [metaTitle, setMetaTitle] = useState('')
  const [metaDescription, setMetaDescription] = useState('')
  const [openGraphImageUrl, setOpenGraphImageUrl] = useState('')
  const [tags, setTags] = useState('')
  const [relatedCourseQuote, setRelatedCourseQuote] = useState(DEFAULT_QUOTE)
  const [relatedCourseId1, setRelatedCourseId1] = useState<number | ''>('')
  const [relatedCourseId2, setRelatedCourseId2] = useState<number | ''>('')

  useEffect(() => {
    ;(async () => {
      try {
        const [metaRes, coursesRes] = await Promise.all([
          AdminApi.getBlogPostsMeta(),
          AdminApi.getCourses(),
        ])
        setTopics(metaRes.data.topics || [])
        setSharingTypes(metaRes.data.sharingTypes || [])
        setCourses(coursesRes.data.items || [])
      } catch {
        toast.error('Không tải được danh mục / khóa học')
      }
    })()
  }, [])

  useEffect(() => {
    if (!isEdit || !postId) return
    ;(async () => {
      setLoading(true)
      try {
        const res = await AdminApi.getBlogPost(postId)
        const p = res.data
        setTitle(p.title || '')
        setTopicId(p.topicId)
        setSharingTypeId(p.sharingTypeId)
        setAuthor(p.author || '')
        setStatus((p.status as AdminBlogPostStatus) || 'INACTIVE')
        setIsFeatured(Boolean(p.isFeatured))
        setShortDescription(p.shortDescription || '')
        setAvatarUrl(p.avatarUrl || '')
        setPostContent(p.postContent || '')
        setSlug(p.slug || '')
        setUuid(p.uuid || '')
        setMetaTitle(p.metaTitle || '')
        setMetaDescription(p.metaDescription || '')
        setOpenGraphImageUrl(p.openGraphImageUrl || '')
        setTags(p.tags || '')
        setRelatedCourseQuote(p.relatedCourseQuote || DEFAULT_QUOTE)
        setRelatedCourseId1(p.relatedCourseId1 || '')
        setRelatedCourseId2(p.relatedCourseId2 || '')
      } catch {
        toast.error('Không tải được bài viết')
        navigate('/admin/posts')
      } finally {
        setLoading(false)
      }
    })()
  }, [isEdit, postId, navigate])

  const uploadAvatar = async (file: File) => {
    setUploadingAvatar(true)
    try {
      const res = await AdminApi.uploadImage(file)
      if (!res.success || !res.data?.url) throw new Error(res.message || 'Upload thất bại')
      setAvatarUrl(res.data.url)
      toast.success('Đã upload ảnh đại diện')
    } catch (err: any) {
      toast.error(err?.message || 'Upload ảnh thất bại')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const buildPayload = (): AdminBlogPostPayload | null => {
    const liveContent = contentEditorRef.current?.getHtml?.() || postContent
    if (liveContent !== postContent) setPostContent(liveContent)
    if (!title.trim()) {
      toast.error('Vui lòng nhập tiêu đề')
      return null
    }
    if (!topicId) {
      toast.error('Vui lòng chọn topic')
      return null
    }
    if (!sharingTypeId) {
      toast.error('Vui lòng chọn loại chia sẻ')
      return null
    }
    return {
      title: title.trim(),
      topicId: Number(topicId),
      sharingTypeId: Number(sharingTypeId),
      author: author.trim() || null,
      status,
      isFeatured,
      shortDescription: shortDescription.trim() || null,
      avatarUrl: avatarUrl.trim() || null,
      postContent: liveContent,
      slug: slug.trim() || null,
      uuid: isEdit ? uuid.trim() || null : uuid.trim() || null,
      metaTitle: metaTitle.trim() || null,
      metaDescription: metaDescription.trim() || null,
      openGraphImageUrl: openGraphImageUrl.trim() || null,
      tags: tags.trim() || null,
      relatedCourseQuote: relatedCourseQuote.trim() || null,
      relatedCourseId1: relatedCourseId1 === '' ? null : Number(relatedCourseId1),
      relatedCourseId2: relatedCourseId2 === '' ? null : Number(relatedCourseId2),
    }
  }

  const handleSave = async (publish?: boolean) => {
    const payload = buildPayload()
    if (!payload) return
    if (publish != null) {
      payload.status = publish ? 'ACTIVE' : 'INACTIVE'
    }
    setSaving(true)
    try {
      if (isEdit && postId) {
        await AdminApi.updateBlogPost(postId, payload)
        toast.success('Đã cập nhật bài viết')
        navigate('/admin/posts')
      } else {
        const res = await AdminApi.createBlogPost(payload)
        toast.success('Đã tạo bài viết')
        navigate(`/admin/posts/${res.data.id}/edit`)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Lưu bài viết thất bại')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Đang tải bài viết...</div>
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/posts" className="text-sm text-emerald-800 hover:underline">
            ← Danh sách bài viết
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-slate-900">
            {isEdit ? 'Sửa bài viết' : 'Thêm bài viết'}
          </h1>
          <p className="text-sm text-slate-500">
            Nội dung HTML editor. Không gắn khóa học / bài test.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnSecondary} onClick={() => navigate('/admin/posts')}>
            Hủy
          </button>
          <button
            type="button"
            disabled={saving}
            className={btnSecondary}
            onClick={() => void handleSave(false)}
          >
            Lưu nháp
          </button>
          <button
            type="button"
            disabled={saving}
            className={btnPrimary}
            onClick={() => void handleSave(true)}
          >
            {saving ? 'Đang lưu...' : 'Lưu & xuất bản'}
          </button>
        </div>
      </div>

      <div className={panelClass}>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className={labelClass}>
              Tiêu đề <span className="text-red-500">*</span>
            </label>
            <input className={fieldClass} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div>
            <label className={labelClass}>
              Topic <span className="text-red-500">*</span>
            </label>
            <select
              className={fieldClass}
              value={topicId}
              onChange={(e) => setTopicId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">— Chọn topic —</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>
              Loại chia sẻ <span className="text-red-500">*</span>
            </label>
            <select
              className={fieldClass}
              value={sharingTypeId}
              onChange={(e) => setSharingTypeId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">— Chọn loại —</option>
              {sharingTypes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Tác giả</label>
            <input className={fieldClass} value={author} onChange={(e) => setAuthor(e.target.value)} />
          </div>

          <div>
            <label className={labelClass}>Trạng thái</label>
            <select
              className={fieldClass}
              value={status}
              onChange={(e) => setStatus(e.target.value as AdminBlogPostStatus)}
            >
              <option value="INACTIVE">Ẩn / nháp</option>
              <option value="ACTIVE">Đang hiện</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>Mô tả ngắn</label>
            <textarea
              className={fieldClass}
              rows={3}
              maxLength={1000}
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>Ảnh đại diện (avatar)</label>
            <div className="flex flex-wrap items-start gap-3">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-20 w-28 rounded object-cover" />
              ) : null}
              <div className="min-w-[220px] flex-1 space-y-2">
                <input
                  className={fieldClass}
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="URL ảnh hoặc upload"
                />
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-emerald-800">
                  <span className="rounded border border-emerald-700 px-3 py-1.5 hover:bg-emerald-50">
                    {uploadingAvatar ? 'Đang upload...' : 'Upload ảnh'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingAvatar}
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) void uploadAvatar(f)
                      e.target.value = ''
                    }}
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:col-span-2">
            <input
              id="isFeatured"
              type="checkbox"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <label htmlFor="isFeatured" className="text-sm text-slate-800">
              Đánh dấu nổi bật (featured)
            </label>
          </div>
        </div>
      </div>

      <div className={panelClass}>
        <label className={labelClass}>Nội dung bài viết (HTML)</label>
        <AdminRichTextEditor
          ref={contentEditorRef}
          value={postContent}
          onChange={setPostContent}
          placeholder="Soạn nội dung bài viết..."
          className="min-h-[320px]"
        />
      </div>

      <div className={panelClass}>
        <h2 className="mb-1 text-sm font-semibold text-slate-800">Khóa học liên quan (đăng ký)</h2>
        <p className="mb-3 text-xs text-slate-500">
          Chọn tối đa 2 khóa học để hiển thị CTA đăng ký dưới bài viết công khai. Không bắt buộc.
        </p>
        <div className="grid gap-4">
          <div>
            <label className={labelClass}>Câu quote / lời mời</label>
            <textarea
              className={fieldClass}
              rows={2}
              maxLength={500}
              value={relatedCourseQuote}
              onChange={(e) => setRelatedCourseQuote(e.target.value)}
              placeholder={DEFAULT_QUOTE}
            />
            <p className="mt-1 text-xs text-slate-400">
              Ví dụ: Muốn tìm hiểu thêm về ... thì tham khảo khóa học sau
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={labelClass}>Khóa học 1</label>
              <select
                className={fieldClass}
                value={relatedCourseId1}
                onChange={(e) =>
                  setRelatedCourseId1(e.target.value ? Number(e.target.value) : '')
                }
              >
                <option value="">— Không chọn —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id} disabled={c.id === relatedCourseId2}>
                    {c.name}
                    {c.code ? ` (${c.code})` : ''}
                    {formatFee(c.fee) ? ` · ${formatFee(c.fee)}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Khóa học 2</label>
              <select
                className={fieldClass}
                value={relatedCourseId2}
                onChange={(e) =>
                  setRelatedCourseId2(e.target.value ? Number(e.target.value) : '')
                }
              >
                <option value="">— Không chọn —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id} disabled={c.id === relatedCourseId1}>
                    {c.name}
                    {c.code ? ` (${c.code})` : ''}
                    {formatFee(c.fee) ? ` · ${formatFee(c.fee)}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className={panelClass}>
        <h2 className="mb-3 text-sm font-semibold text-slate-800">SEO & URL</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className={labelClass}>Slug</label>
            <input
              className={fieldClass}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Tự sinh từ tiêu đề nếu để trống"
            />
          </div>
          <div>
            <label className={labelClass}>UUID (public key URL)</label>
            <input
              className={fieldClass}
              value={uuid}
              onChange={(e) => setUuid(e.target.value)}
              placeholder="Tự sinh nếu để trống"
            />
          </div>
          <div>
            <label className={labelClass}>Meta title (≤70)</label>
            <input
              className={fieldClass}
              maxLength={70}
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Tags (phân cách bằng dấu phẩy)</label>
            <input className={fieldClass} value={tags} onChange={(e) => setTags(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Meta description (≤200)</label>
            <textarea
              className={fieldClass}
              rows={2}
              maxLength={200}
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Open Graph image URL</label>
            <input
              className={fieldClass}
              value={openGraphImageUrl}
              onChange={(e) => setOpenGraphImageUrl(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pb-8">
        <button type="button" className={btnSecondary} onClick={() => navigate('/admin/posts')}>
          Hủy
        </button>
        <button
          type="button"
          disabled={saving}
          className={btnPrimary}
          onClick={() => void handleSave()}
        >
          {saving ? 'Đang lưu...' : 'Lưu'}
        </button>
      </div>
    </div>
  )
}

export default AdminPostFormPage
